import { useEffect, useState } from 'react'
import {
  GRAPH_SAMPLE_INTERVAL_MS,
  LIVE_TICK_INTERVAL_MS,
  MAX_SAMPLES
} from './performanceMonitorBar.constants'
import { MemorySample, PerformanceMemory } from './performanceMonitorBar.types'

const readMemory = (): PerformanceMemory | null => {
  if (typeof performance === 'undefined' || !('memory' in performance)) {
    return null
  }
  try {
    // performance.memory can exist but not be the Chromium stats object —
    // Playwright's mocked clock replaces it with a function — so validate the
    // shape rather than the key.
    const memory = performance.memory as PerformanceMemory | undefined
    if (typeof memory?.usedJSHeapSize !== 'number') {
      return null
    }
    return memory
  } catch {
    return null
  }
}

export const useMemoryUsage = () => {
  const [memory, setMemory] = useState<PerformanceMemory | null>(() =>
    readMemory()
  )
  const [history, setHistory] = useState<MemorySample[]>(() => {
    const initial = readMemory()
    if (initial === null) return []
    return [{ timestamp: Date.now(), usedJSHeapSize: initial.usedJSHeapSize }]
  })

  useEffect(() => {
    const liveInterval = setInterval(() => {
      setMemory(readMemory())
    }, LIVE_TICK_INTERVAL_MS)

    const sampleInterval = setInterval(() => {
      const next = readMemory()
      if (next === null) return
      setHistory(prev => {
        const sample: MemorySample = {
          timestamp: Date.now(),
          usedJSHeapSize: next.usedJSHeapSize
        }
        const updated = [...prev, sample]
        return updated.length > MAX_SAMPLES
          ? updated.slice(updated.length - MAX_SAMPLES)
          : updated
      })
    }, GRAPH_SAMPLE_INTERVAL_MS)

    return () => {
      clearInterval(liveInterval)
      clearInterval(sampleInterval)
    }
  }, [])

  return { memory, history }
}
